/**
 * Distance helpers for city-distance game modes. Computation is in km
 * (the natural haversine output); display layer converts to miles when
 * the mode's `unitSystem` says so.
 *
 * The engine ships its own haversine in `geo/distance.ts` for any
 * polygon-related math; this re-exports under a friendlier name and
 * adds the unit-conversion that game modes care about.
 */

const KM_TO_MILES = 0.621371;

/**
 * Great-circle distance between two lat/lon points in kilometers.
 * Standard haversine — well-conditioned at small distances and
 * accurate enough for game-mode scoring (sub-meter error at 10000 km).
 */
export function haversineKm(
  a: { lat: number; lon: number },
  b: { lat: number; lon: number },
): number {
  const R = 6371; // mean Earth radius in km
  const toRad = (deg: number): number => (deg * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lon - a.lon);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const sinDLat = Math.sin(dLat / 2);
  const sinDLon = Math.sin(dLon / 2);
  const h =
    sinDLat * sinDLat + Math.cos(lat1) * Math.cos(lat2) * sinDLon * sinDLon;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Convert km → miles. */
export function kmToMiles(km: number): number {
  return km * KM_TO_MILES;
}

/**
 * Format a distance in the requested unit system. Tightens the precision
 * at small values (whole units < 100, one decimal < 10).
 */
export function formatDistance(km: number, units: 'miles' | 'km'): string {
  const value = units === 'miles' ? kmToMiles(km) : km;
  const unitLabel = units === 'miles' ? 'mi' : 'km';
  const formatted =
    value < 10 ? value.toFixed(1) : Math.round(value).toLocaleString();
  return `${formatted} ${unitLabel}`;
}
